"use client";

import { Core } from "@faire/design-tokens";
import { useFormattedOfferedMaxTerms } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useFormattedOfferedMaxTerms";
import { useAssignOwfTermsIncrease } from "@faire/retailer-visitor-shared/settings/useSettingOwfTermsIncrease";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { UpsellContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/upsell-template/UpsellContainer";
import { UpsellLayout } from "@faire/retailer-visitor-shared/views/SignUp/_components/upsell-template/UpsellLayout";
import { useSignUpOwFApplicationStatus } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpOwFAppStatus";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import { useIsVerticalizedSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/useIsVerticalizedSignUp";
import { getTermsAndOwfImage } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/useSignUpImage";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useHandleOwFSkip } from "@faire/retailer-visitor-shared/views/SignUp/useHandleOwFSkip";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { trackOpenWithFaireOwfSignUpFlowUpsellContinueClick } from "@faire/web-api--source/events/openWithFaire/click/owfSignUpFlowUpsellContinue";
import { trackOpenWithFaireOwfSignUpFlowUpsellView } from "@faire/web-api--source/events/openWithFaire/view/owfSignUpFlowUpsell";
import React, { useEffect } from "react";

export const OwFIntroStep = () => {
  const { strictLocalize } = useStrictLocalization();
  const [{ isVerticalizedSignUp }] = useSignUpRequest(["isVerticalizedSignUp"]);
  const verticalizedImage = getTermsAndOwfImage(strictLocalize);
  const size = useResponsiveValue({
    mobileAndAbove: MOBILE_SIZE,
    tabletAndAbove: DESKTOP_SIZE,
  });
  const imageUrl = useResponsiveValue({
    mobileAndAbove: MOBILE_HERO_IMAGE,
    tabletAndAbove: DESKTOP_HERO_IMAGE,
  });
  const verticalizedImageryInSignUp = useIsVerticalizedSignUp();

  const { canApply } = useSignUpOwFApplicationStatus();
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  const assignOwfTermsIncrease = useAssignOwfTermsIncrease();
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.OWF_UPSELL
  );
  const loadTimingRef = useSignUpUPLT({ step: Step.OWF_UPSELL });

  const handleSkip = useHandleOwFSkip();

  const handleContinue = () => {
    trackOpenWithFaireOwfSignUpFlowUpsellContinueClick();
    saveAndGoToNextStep(undefined);
  };

  useEffect(() => {
    assignOwfTermsIncrease();
  }, [assignOwfTermsIncrease]);

  useEffect(() => {
    if (!canApply) {
      saveAndGoToNextStep(undefined, { skipOwF: true });
    }
  }, [canApply, saveAndGoToNextStep]);

  if (verticalizedImageryInSignUp) {
    return (
      <SignupFormContainer>
        <OwfIntroContent
          formattedOfferedMaxTerms={formattedOfferedMaxTerms}
          handleContinue={handleContinue}
          handleSkip={handleSkip}
        />
      </SignupFormContainer>
    );
  }

  return (
    <UpsellLayout backgroundColor={Core.surface.info} onClose={handleSkip}>
      <UpsellContainer
        loadTimingRef={loadTimingRef}
        imageUrl={
          isVerticalizedSignUp ? verticalizedImage.image?.url : imageUrl
        }
        size={
          isVerticalizedSignUp
            ? {
                width: verticalizedImage.image?.width ?? 0,
                height: verticalizedImage.image?.height ?? 0,
              }
            : size
        }
        mobileOffset="-5%"
      >
        <OwfIntroContent
          formattedOfferedMaxTerms={formattedOfferedMaxTerms}
          handleContinue={handleContinue}
          handleSkip={handleSkip}
        />
      </UpsellContainer>
      <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }} />
    </UpsellLayout>
  );
};

const OwfIntroContent: React.FC<{
  formattedOfferedMaxTerms: string;
  handleContinue: () => void;
  handleSkip: () => void;
}> = ({ formattedOfferedMaxTerms, handleContinue, handleSkip }) => {
  const ref = useDoActionWhenVisible(() =>
    trackOpenWithFaireOwfSignUpFlowUpsellView()
  );

  return (
    <>
      <Typography ref={ref} variant="displaySSerifRegular">
        <StrictLocalMsg
          defaultMessage="You’re eligible for up to {offeredMaxTermsMoney} in payment terms"
          description={{
            text: "Title explaining that the retailer can have payment terms up to an amount",
            img: "https://cdn.faire.com/fastly/4cb1c1fa1e12d676474108a2ac94fde05dd2dbb346e1cbea86ffa175a8b0d308.png",
          }}
          values={{
            offeredMaxTermsMoney: formattedOfferedMaxTerms,
          }}
        />
      </Typography>
      <Spacer height={12} />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Our Open with Faire program is for new store owners like you! It helps cover the costs of your inventory needs."
          description={{
            text: "A nudge incentivizing new retailers to join Open With Faire program explaining the value of the program",
            img: "https://cdn.faire.com/fastly/4cb1c1fa1e12d676474108a2ac94fde05dd2dbb346e1cbea86ffa175a8b0d308.png",
          }}
        />
      </Typography>
      <Spacer height={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }} />
      <Button
        variant="primary"
        onClick={handleContinue}
        data-test-id="apply-now"
      >
        <StrictLocalMsg
          defaultMessage="Apply now"
          description={{
            text: "Button to apply for Open With Faire program",
            img: "https://cdn.faire.com/fastly/4cb1c1fa1e12d676474108a2ac94fde05dd2dbb346e1cbea86ffa175a8b0d308.png",
          }}
        />
      </Button>
      <Spacer height="3x" />
      <Button
        variant="tertiary"
        onClick={handleSkip}
        style={{ alignSelf: "center" }}
        data-test-id="owf-intro-cta-skip"
      >
        <StrictLocalMsg
          defaultMessage="Skip"
          description={{
            text: "Button to skip Open with Faire upsell",
            img: "https://cdn.faire.com/fastly/ad0b904095c09b483f3cbe37df1708afa1dfc48eb80ebf0de5efaeb5df88e68e.png",
          }}
        />
      </Button>
    </>
  );
};

const MOBILE_HERO_IMAGE =
  "https://cdn.faire.com/fastly/dfb6ff3e0a4733f46543ddcac3d4c634947e04a1c95a5a5957b90377e02f5b13.jpeg";
const MOBILE_SIZE = {
  width: 400,
  height: 267,
};

const DESKTOP_HERO_IMAGE =
  "https://cdn.faire.com/fastly/e4c895e312d0288e9181eaf281f6cd41446cc46eb89cbd8347f90976fff01bad.jpeg";
const DESKTOP_SIZE = {
  width: 608,
  height: 280,
};
